import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './i18n';
import '@/App.css';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import Quizzes from './pages/Quizzes';
import Leaderboard from './pages/Leaderboard';
import Profile from './pages/Profile';
import QuizBuilder from './pages/QuizBuilder';
import WheelGame from './pages/WheelGame';
import WheelGameHub from './pages/WheelGameHub';
import PuzzleBuilder from './pages/PuzzleBuilder';
import PuzzleGame from './pages/PuzzleGame';
import OpenBoxBuilder from './pages/OpenBoxBuilder';
import OpenBoxGame from './pages/OpenBoxGame';
import OpenBoxHub from './pages/OpenBoxHub';
import FlashcardBuilder from './pages/FlashcardBuilder';
import FlashcardGame from './pages/FlashcardGame';
import FlashcardHub from './pages/FlashcardHub';
import AnagramBuilder from './pages/AnagramBuilder';
import AnagramGame from './pages/AnagramGame';
import AirplaneQuizGame from './pages/AirplaneQuizGame';
import GameHub from './pages/GameHub';
import CheeseChaseGame from './pages/CheeseChaseGame';
import FishingQuizGame from './pages/FishingQuizGame';
import FishingQuizHub from './pages/FishingQuizHub';
import QuizBlasterGame from './pages/QuizBlasterGame';
import QuizBlasterHub from './pages/QuizBlasterHub';
import SaveTheRabbitGame from './pages/SaveTheRabbitGame';
import SaveTheRabbitHub from './pages/SaveTheRabbitHub';
import SillyGapFillGame from './pages/SillyGapFillGame';
import SillyGapFillHub from './pages/SillyGapFillHub';
import KidsSortingGame from './pages/KidsSortingGame';
import KidsSortingHub from './pages/KidsSortingHub';
import ClassroomQuizRaceGame from './pages/ClassroomQuizRaceGame';
import ClassroomQuizRaceHub from './pages/ClassroomQuizRaceHub';
import WordOrderGame from './pages/WordOrderGame';
import WordOrderHub from './pages/WordOrderHub';
import PenaltyQuizHub from './pages/PenaltyQuizHub';
import PenaltyQuizGame from './pages/PenaltyQuizGame';
import RocketGameHub from './pages/RocketGameHub';
import RocketGame from './pages/RocketGame';
import WhatsInTheBoxHub from './pages/WhatsInTheBoxHub';
import WhatsInTheBoxGame from './pages/WhatsInTheBoxGame';
import OurGames from './pages/OurGames';
import Subscribe from './pages/Subscribe';
import AdminPanel from './pages/AdminPanel';
import Navbar from './components/Navbar';
import Mascot from './components/Mascot';
import { Toaster } from 'sonner';
import { SubscriptionProvider } from './context/SubscriptionContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthContext = React.createContext(null);

function AuthCheck({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const sessionId = new URLSearchParams(location.hash.substring(1)).get('session_id');
    
    if (sessionId) {
      processSession(sessionId);
    } else {
      checkExistingSession();
    }
  }, [location]);
  
  const processSession = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
      setUser(response.data.user);
      window.location.hash = '';
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Session error:', error);
      setLoading(false);
    }
  };
  
  const checkExistingSession = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      console.log('No active session');
    } finally {
      setLoading(false);
    }
  };
  
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }
  
  return (
    <AuthContext.Provider value={{ user, setUser, logout }}>
      <SubscriptionProvider>
        {children}
      </SubscriptionProvider>
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user } = React.useContext(AuthContext);
  return user ? children : <Navigate to="/" replace />;
}

function App() {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    document.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
  }, [i18n.language]);
  
  return (
    <BrowserRouter>
      <AuthCheck>
        <div className="App">
          <Toaster position="top-center" richColors />
          <Navbar />
          <Mascot />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/quizzes" element={<ProtectedRoute><Quizzes /></ProtectedRoute>} />
            <Route path="/wheel" element={<ProtectedRoute><WheelGame /></ProtectedRoute>} />
            <Route path="/wheel-hub" element={<ProtectedRoute><WheelGameHub /></ProtectedRoute>} />
            <Route path="/wheel-game" element={<ProtectedRoute><WheelGame /></ProtectedRoute>} />
            <Route path="/puzzle" element={<ProtectedRoute><PuzzleGame /></ProtectedRoute>} />
            <Route path="/open-box" element={<ProtectedRoute><OpenBoxGame /></ProtectedRoute>} />
            <Route path="/open-box-hub" element={<ProtectedRoute><OpenBoxHub /></ProtectedRoute>} />
            <Route path="/flashcards" element={<ProtectedRoute><FlashcardGame /></ProtectedRoute>} />
            <Route path="/flashcard-hub" element={<ProtectedRoute><FlashcardHub /></ProtectedRoute>} />
            <Route path="/flashcard-game" element={<ProtectedRoute><FlashcardGame /></ProtectedRoute>} />
            <Route path="/anagram" element={<ProtectedRoute><AnagramGame /></ProtectedRoute>} />
            <Route path="/airplane-quiz" element={<ProtectedRoute><AirplaneQuizGame /></ProtectedRoute>} />
            <Route path="/game-hub" element={<ProtectedRoute><GameHub /></ProtectedRoute>} />
            <Route path="/cheese-chase-game" element={<ProtectedRoute><CheeseChaseGame /></ProtectedRoute>} />
            <Route path="/fishing-quiz-hub" element={<ProtectedRoute><FishingQuizHub /></ProtectedRoute>} />
            <Route path="/fishing-quiz-game" element={<ProtectedRoute><FishingQuizGame /></ProtectedRoute>} />
            <Route path="/quiz-blaster-hub" element={<ProtectedRoute><QuizBlasterHub /></ProtectedRoute>} />
            <Route path="/quiz-blaster-game" element={<ProtectedRoute><QuizBlasterGame /></ProtectedRoute>} />
            <Route path="/save-the-rabbit-hub" element={<ProtectedRoute><SaveTheRabbitHub /></ProtectedRoute>} />
            <Route path="/save-the-rabbit-game" element={<ProtectedRoute><SaveTheRabbitGame /></ProtectedRoute>} />
            <Route path="/silly-gap-fill-hub" element={<ProtectedRoute><SillyGapFillHub /></ProtectedRoute>} />
            <Route path="/silly-gap-fill-game" element={<ProtectedRoute><SillyGapFillGame /></ProtectedRoute>} />
            <Route path="/kids-sorting-hub" element={<ProtectedRoute><KidsSortingHub /></ProtectedRoute>} />
            <Route path="/kids-sorting-game" element={<ProtectedRoute><KidsSortingGame /></ProtectedRoute>} />
            <Route path="/classroom-quiz-race-hub" element={<ProtectedRoute><ClassroomQuizRaceHub /></ProtectedRoute>} />
            <Route path="/classroom-quiz-race-game" element={<ProtectedRoute><ClassroomQuizRaceGame /></ProtectedRoute>} />
            <Route path="/word-order-hub" element={<ProtectedRoute><WordOrderHub /></ProtectedRoute>} />
            <Route path="/word-order-game" element={<ProtectedRoute><WordOrderGame /></ProtectedRoute>} />
            <Route path="/penalty-quiz-hub" element={<ProtectedRoute><PenaltyQuizHub /></ProtectedRoute>} />
            <Route path="/penalty-quiz-game" element={<ProtectedRoute><PenaltyQuizGame /></ProtectedRoute>} />
            <Route path="/rocket-game-hub" element={<ProtectedRoute><RocketGameHub /></ProtectedRoute>} />
            <Route path="/rocket-game" element={<ProtectedRoute><RocketGame /></ProtectedRoute>} />
            <Route path="/whats-in-the-box-hub" element={<ProtectedRoute><WhatsInTheBoxHub /></ProtectedRoute>} />
            <Route path="/whats-in-the-box-game" element={<ProtectedRoute><WhatsInTheBoxGame /></ProtectedRoute>} />
            <Route path="/our-games" element={<ProtectedRoute><OurGames /></ProtectedRoute>} />
            <Route path="/subscribe" element={<Subscribe />} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/quiz-builder" element={<ProtectedRoute><QuizBuilder /></ProtectedRoute>} />
            <Route path="/puzzle-builder" element={<ProtectedRoute><PuzzleBuilder /></ProtectedRoute>} />
            <Route path="/open-box-builder" element={<ProtectedRoute><OpenBoxBuilder /></ProtectedRoute>} />
            <Route path="/flashcard-builder" element={<ProtectedRoute><FlashcardBuilder /></ProtectedRoute>} />
            <Route path="/anagram-builder" element={<ProtectedRoute><AnagramBuilder /></ProtectedRoute>} />
            {/* Admin Panel - Protected route with server-side admin check */}
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
          </Routes>
        </div>
      </AuthCheck>
    </BrowserRouter>
  );
}

export default App;
